"use client";

import { useEffect } from "react";
import {
  TOUCH_TAP_SLOP_PX,
  focusTouchEditableTextField,
  isTouchTap,
  resolveTouchEditableTextField,
  type TouchTapPoint,
} from "@/lib/virtualspace/touch-text-field-focus";

type PendingTap = { field: HTMLElement; start: TouchTapPoint };

function pointOf(event: PointerEvent): TouchTapPoint {
  return {
    pointerId: event.pointerId,
    x: event.clientX,
    y: event.clientY,
    time: event.timeStamp,
  };
}

/**
 * App-wide first-tap focus for text fields on coarse pointers.
 * Mounted once from the root layout so SpaceManager, Spatialist,
 * and SpaceStation share the same behavior.
 *
 * Focus is applied when a touch *ends* as a tap on the field. Focusing on
 * touch start raised the keyboard whenever a finger began a scroll on top
 * of a field (the Spatialist mission search box).
 */
export default function TouchTextFieldFocus() {
  useEffect(() => {
    let pending: PendingTap | null = null;

    const onPointerDown = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      const field = resolveTouchEditableTextField(event.target);
      const additionalFinger = pending != null && event.isPrimary === false;
      pending =
        field && !additionalFinger && document.activeElement !== field
          ? { field, start: pointOf(event) }
          : null;
    };

    const onPointerMove = (event: PointerEvent) => {
      if (!pending || event.pointerType !== "touch") return;
      const { start } = pending;
      if (
        Math.hypot(event.clientX - start.x, event.clientY - start.y) >
        TOUCH_TAP_SLOP_PX
      ) {
        pending = null;
      }
    };

    const onPointerUp = (event: PointerEvent) => {
      if (event.pointerType !== "touch") return;
      const tap = pending;
      pending = null;
      if (!tap || !isTouchTap(tap.start, pointOf(event))) return;
      if (resolveTouchEditableTextField(event.target) !== tap.field) return;
      focusTouchEditableTextField(tap.field);
    };

    const cancel = () => {
      pending = null;
    };

    const options = { capture: true } as const;
    const passive = { capture: true, passive: true } as const;
    document.addEventListener("pointerdown", onPointerDown, passive);
    document.addEventListener("pointermove", onPointerMove, passive);
    document.addEventListener("pointerup", onPointerUp, options);
    document.addEventListener("pointercancel", cancel, options);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, passive);
      document.removeEventListener("pointermove", onPointerMove, passive);
      document.removeEventListener("pointerup", onPointerUp, options);
      document.removeEventListener("pointercancel", cancel, options);
    };
  }, []);

  return null;
}
